import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Spacer } from "@aviary";
import { GoogleSSOButton } from "@shared/components";
import { useParams } from "@shared/react-router-dom";
import {
  NewToFullscriptSignUp,
  SignInForm,
  SignInWrapper,
  SSOErrorBox,
  FormWrapper,
} from "@unauthenticated/shared/components";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import {
  StoreOffersPill,
  StoreLogo,
  SignUpModalContainer,
  SignUpModalHeader,
  SignUpModalTitle,
  SignUpModalSubtitle,
  SignUpModalFooter,
} from "@unauthenticated/signupSignin/components";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { useTreatmentPlanTerm } from "@unauthenticated/signupSignin/hooks/useTreatmentPlanTerm";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  ssoErrors?: string;
}

const RxSignInModalMain: FC<Props> = ({ patientAttributes, additionalAttributes, ssoErrors }) => {
  const treatmentPlanTerm = useTreatmentPlanTerm();
  const { t } = useTranslation();
  const {
    storeLogo,
    storeName,
    email,
    pracName,
    firstName,
    treatmentPlanSlug,
    labRecommendationId,
    storeSlug,
  } = patientAttributes;

  // TODO: Ryan - Remove the ignore once the react-router v6 upgrade is completed!
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { practitionerSlug, tpSlug } = useParams<"practitionerSlug" | "tpSlug">();

  const renderSSOErrors = () => {
    if (ssoErrors === "rx_email_mismatch") {
      return (
        <SSOErrorBox
          isColor="danger"
          errorMessage={t(l.patientRxAuth.SSOEmailNotAssociated, { treatmentPlanTerm })}
          hideSubErrorMessage
        />
      );
    }
  };

  const signupModalSubtitle = labRecommendationId
    ? t(l.patientRxAuth.SignInWelcomeMessage, {
        firstName,
        pracName,
      })
    : t(l.patientRxAuth.SignInWelcomeMessage, {
        firstName,
        treatmentPlanTerm,
        pracName,
      });

  return (
    <>
      <SignUpModalHeader>
        <StoreLogo storeLogo={storeLogo} storeName={storeName} />
        <SignUpModalTitle title={storeName} />
        <StoreOffersPill patientAttributes={patientAttributes} />
        <SignUpModalSubtitle subtitle={signupModalSubtitle} />
      </SignUpModalHeader>
      <SignUpModalContainer>
        <FormWrapper>
          <SignInWrapper>
            {ssoErrors && renderSSOErrors()}
            <GoogleSSOButton
              isOrTextRendered
              signUpPage="rx_patient_sign_in_page"
              pageVersion="react_with_sso"
              treatmentPlanSlug={treatmentPlanSlug}
              labRecommendationId={labRecommendationId}
              storeSlug={storeSlug}
            />
            <SignInForm
              initialEmail={email}
              additionalAttributes={additionalAttributes}
              forgotPasswordLink={authRoutes.rx_sign_in_forgot_password(practitionerSlug, tpSlug)}
            />
            <Spacer height="doubleHalf" />
            <NewToFullscriptSignUp userType="patient" />
          </SignInWrapper>
        </FormWrapper>
      </SignUpModalContainer>
      <SignUpModalFooter />
    </>
  );
};

export { RxSignInModalMain };
